/**
 * Per-language prose for the MarketingContent component. Kept in its
 * own file so i18n.ts (UI strings) stays focused on the short labels
 * and tooltips scattered across many components — the marketing
 * content is paragraph-heavy and benefits from being in one place per
 * language.
 *
 * Translations are functional rather than literary. SEO target keywords
 * are preserved per language so each translation ranks for the same
 * intent: "free private receipt scanner", "receipt to Excel/CSV/ZIP",
 * "OCR locally in browser". Native-speaker review welcome.
 *
 * The English copy is the source of truth — when something changes
 * here, the FAQ + HowTo JSON-LD in `index.html` mirrors English
 * verbatim. Phase 4 (per-language URLs) ships per-language `index.html`
 * shells so the structured data matches the rendered language too.
 */

import type { UiLangCode } from '../lib/i18n';

export interface MarketingStrings {
  hero: {
    title: string;
    subtitle: string;
  };
  howItWorksHeading: string;
  steps: Array<{ title: string; body: string }>;
  whyLocalHeading: string;
  whyLocalParagraphs: [string, string, string];
  useCasesHeading: string;
  useCases: Array<{ key: 'tax' | 'expense' | 'freelance' | 'split'; title: string; body: string }>;
  faqHeading: string;
  faq: Array<{ q: string; a: string }>;
  feedback: {
    heading: string;
    /** Use the literal token `{email}` where the contact address should
     *  appear; the renderer splits on it and substitutes a mailto link. */
    body: string;
  };
}

const eng: MarketingStrings = {
  hero: {
    title: 'Free private receipt scanner — convert photos and PDFs to CSV, Excel, ZIP',
    subtitle:
      'Receipt Ripper turns receipt photos and PDFs into structured data — vendor, date, line items, totals, tax — entirely in your browser. No upload, no signup, no account. Free forever.',
  },
  howItWorksHeading: 'How it works',
  steps: [
    {
      title: '1. Drop the receipt',
      body:
        "Drag a photo (JPG, PNG, HEIC, even an iPhone HEIF), drop a PDF, or paste from your clipboard. Multiple receipts at once work too — drop a batch or even a ZIP archive of last quarter's photos.",
    },
    {
      title: '2. OCR runs in your browser',
      body:
        'Tesseract.js processes each receipt inside a Web Worker. Photos get auto-cropped, perspective-corrected, and read end-to-end on your device. Nothing uploads to any server.',
    },
    {
      title: '3. Review and export',
      body:
        'Edit any field that came through wrong, tag receipts with categories, then download CSV, Excel (.xlsx), or a ZIP that bundles a workbook plus every original image — ready for your bookkeeper.',
    },
  ],
  whyLocalHeading: 'Why local OCR matters',
  whyLocalParagraphs: [
    "Most receipt scanners work by uploading your image to a cloud OCR pipeline. Their server receives the photo, runs OCR remotely, and returns structured data. That works, but it means receipts sit on someone else's hardware: payment-card last-fours, store locations, purchase patterns, dates and times you were somewhere.",
    "Receipt Ripper takes the opposite stance. The OCR runs on your device. The parsed data lives in your browser tab and gets exported straight to your filesystem. There is no API call carrying receipt bytes — it's structurally impossible because the site's Content Security Policy refuses external network connections from anything that could carry receipt content.",
    "That makes Receipt Ripper a fit for anyone whose receipt data is sensitive: business owners with expense patterns they don't want indexed, contractors whose client billing is private, individuals doing tax prep, anyone in a high-regulation industry, and anyone who simply prefers their stuff stays theirs.",
  ],
  useCasesHeading: 'Use cases',
  useCases: [
    {
      key: 'tax',
      title: 'Tax preparation',
      body:
        "Year-end tax filing means turning a shoebox of receipts into deductible-expense rows. Receipt Ripper batches a quarter's worth in minutes. Subtotal, tax (VAT / IVA / TVA / GST), and total are extracted per receipt and summed in the Excel export — your accountant gets a workbook that pivots cleanly.",
    },
    {
      key: 'expense',
      title: 'Expense reports',
      body:
        'Reimbursable spend tracking on the road or after a client trip. Drag the receipts off your phone, edit the vendor names if OCR mangled them, hit Export. The ZIP bundle gives your finance team a workbook plus every original image attached — no separate "find the matching photo" step.',
    },
    {
      key: 'freelance',
      title: 'Freelancer bookkeeping',
      body:
        'Self-employed bookkeeping is a slow accumulation of small purchases. Drop each receipt in as you get it, tag with project or client, and the running batch sums the relevant totals. Toggle "Exclude tax from exports" if your jurisdiction frowns on handing tax-itemised data to third parties.',
    },
    {
      key: 'split',
      title: 'Splitting bills',
      body:
        'Group dinner where everyone wants their slice itemised. Receipt Ripper extracts each line item with quantity and price, so you can split by what each person actually ordered instead of dividing the total. Works on Spanish, French, German, Italian, Portuguese, and Dutch receipts as well as English.',
    },
  ],
  faqHeading: 'Frequently asked questions',
  faq: [
    {
      q: 'Is Receipt Ripper really free?',
      a: "Yes. There's no signup, no paid tier, no usage limit. The site runs on advertising plus optional donations — that's it. The full feature set is available on every visit.",
    },
    {
      q: 'Where do my receipts go?',
      a: "Nowhere. The image is decoded, OCR'd, parsed, and exported entirely inside your browser. There's no upload step and no server-side processing — open your DevTools Network panel during a scan and you'll see no request carries the image bytes anywhere.",
    },
    {
      q: 'What languages and currencies does it support?',
      a:
        'Receipt Ripper is built and tested on receipts in English, Spanish, French, German, Dutch, Italian, and Portuguese. Currency detection covers EUR, USD, GBP, CHF, JPY, and the spelled-out forms (Euro, Dollar, Franc).',
    },
    {
      q: 'Does it work on iPhone photos?',
      a:
        'Yes. iPhone HEIC / HEIF photos are decoded client-side via libheif-js and processed identically to JPGs. The mobile camera button in the dropzone opens the rear camera directly so you can scan a receipt and immediately see it parsed.',
    },
    {
      q: 'Can I use it offline?',
      a:
        "After the first visit, yes. The Tesseract OCR engine and language packs cache in your browser, so subsequent scans don't need a network connection. The ad banner and analytics will be missing offline; the receipt-processing functionality is unaffected.",
    },
    {
      q: 'Can I use it for taxes?',
      a:
        'Many do. The XLSX export\'s "Summary" sheet groups receipts by category and currency with grand totals; the "per-receipt breakdown" sheets give your accountant the line-item detail they need. The "exclude tax from exports" toggle hides VAT/IVA columns when handing data to third parties is detrimental in your jurisdiction.',
    },
    {
      q: 'How do you handle bad photos?',
      a:
        "Wrinkled paper, fold marks, slight tilts, and most photographing-on-a-textured-surface cases are handled automatically — there's a dual-tier corner-detection pipeline (Hough lines first, edge-contour flood-fill fallback). For receipts the auto-detect can't crop, every session gets a \"Re-crop manually\" button that opens an interactive corner picker.",
    },
    {
      q: 'What about multiple receipts at once?',
      a:
        'Drop them all on the dropzone, or drop a ZIP archive containing the images / PDFs. They process in parallel through a worker pool sized to your hardware (2 workers on mobile / low-RAM, hardwareConcurrency − 2 on desktop). The "Download ZIP" export bundles every processed image alongside a single workbook — handy for archiving a quarter at a time.',
    },
    {
      q: 'Why does the download wait 5 seconds?',
      a:
        "Free, ad-supported tools cost something to run. The pre-export prompt offers a one-click donation via Stripe Payment Link before your file downloads. Skip is right there if you're in a hurry — the download fires the moment the countdown ends regardless.",
    },
    {
      q: 'Is the source code open?',
      a:
        "The OCR engine (Tesseract.js), PDF reader (Mozilla's pdf.js), HEIC decoder (libheif-js), and Excel writer (SheetJS xlsx) are all open source — see the in-app license link in the footer for the full attribution list.",
    },
  ],
  feedback: {
    heading: 'Feature requests & tricky receipts',
    body:
      "Have an idea for a feature, or a receipt that didn't scan correctly? Email {email} — every problem receipt you share helps us tune the detection engine.",
  },
};

const spa: MarketingStrings = {
  hero: {
    title: 'Escáner de recibos privado y gratuito — convierte fotos y PDFs a CSV, Excel o ZIP',
    subtitle:
      'Receipt Ripper convierte fotos y PDFs de recibos en datos estructurados — proveedor, fecha, líneas, totales, IVA — completamente en tu navegador. Sin subidas, sin registro, sin cuenta. Gratis para siempre.',
  },
  howItWorksHeading: 'Cómo funciona',
  steps: [
    {
      title: '1. Suelta el recibo',
      body:
        'Arrastra una foto (JPG, PNG, HEIC, incluso HEIF de iPhone), suelta un PDF o pega desde el portapapeles. Varios recibos a la vez también funcionan — un lote o incluso un archivo ZIP con las fotos del último trimestre.',
    },
    {
      title: '2. El OCR corre en tu navegador',
      body:
        'Tesseract.js procesa cada recibo dentro de un Web Worker. Las fotos se recortan automáticamente, se corrige la perspectiva y se leen de extremo a extremo en tu dispositivo. Nada se sube a ningún servidor.',
    },
    {
      title: '3. Revisa y exporta',
      body:
        'Edita cualquier campo que haya salido mal, etiqueta los recibos con categorías y descarga CSV, Excel (.xlsx) o un ZIP que agrupa el libro de cálculo más cada imagen original — listo para tu contable.',
    },
  ],
  whyLocalHeading: 'Por qué importa el OCR local',
  whyLocalParagraphs: [
    'La mayoría de los escáneres de recibos funcionan subiendo tu imagen a una infraestructura OCR en la nube. Su servidor recibe la foto, ejecuta el OCR remotamente y devuelve los datos estructurados. Funciona, pero significa que tus recibos quedan en hardware ajeno: últimos cuatro dígitos de tarjetas, ubicaciones de tiendas, patrones de compra, fechas y horas en las que estuviste en algún sitio.',
    'Receipt Ripper toma la postura opuesta. El OCR se ejecuta en tu dispositivo. Los datos analizados viven en tu pestaña del navegador y se exportan directamente a tu sistema de archivos. No hay ninguna llamada a una API que transporte bytes de recibos — es estructuralmente imposible porque la Content Security Policy del sitio rechaza cualquier conexión de red externa que pudiera transportar contenido de recibos.',
    'Eso convierte a Receipt Ripper en una opción ideal para quien tenga datos de recibos sensibles: dueños de negocio con patrones de gasto que no quieren indexados, autónomos cuya facturación a clientes es privada, particulares haciendo la declaración de impuestos, profesionales en sectores muy regulados, y cualquiera que simplemente prefiera que sus cosas sigan siendo suyas.',
  ],
  useCasesHeading: 'Casos de uso',
  useCases: [
    {
      key: 'tax',
      title: 'Declaración de impuestos',
      body:
        'La declaración anual implica convertir una caja de recibos en filas de gastos deducibles. Receipt Ripper procesa el equivalente a un trimestre en minutos. La base imponible, el IVA y el total se extraen por recibo y se suman en la exportación a Excel — tu contable recibe un libro que pivota limpiamente.',
    },
    {
      key: 'expense',
      title: 'Notas de gastos',
      body:
        'Seguimiento de gastos reembolsables tras un viaje de cliente o de trabajo. Arrastra los recibos desde el móvil, corrige el nombre del proveedor si el OCR lo mutiló, pulsa Exportar. El ZIP da a tu equipo financiero un libro con cada imagen original adjunta — sin el paso "buscar la foto correspondiente".',
    },
    {
      key: 'freelance',
      title: 'Contabilidad de autónomos',
      body:
        'La contabilidad de un autónomo es la acumulación lenta de compras pequeñas. Suelta cada recibo en cuanto lo recibas, etiquétalo por proyecto o cliente, y el lote suma los totales relevantes. Activa "Excluir IVA de las exportaciones" si tu jurisdicción no recomienda entregar datos fiscalmente desglosados a terceros.',
    },
    {
      key: 'split',
      title: 'Dividir cuentas',
      body:
        'Cena de grupo en la que cada uno quiere su parte detallada. Receipt Ripper extrae cada línea con cantidad y precio, así puedes dividir por lo que cada persona pidió en lugar de partir el total. Funciona en recibos en español, francés, alemán, italiano, portugués y neerlandés además del inglés.',
    },
  ],
  faqHeading: 'Preguntas frecuentes',
  faq: [
    {
      q: '¿Receipt Ripper es realmente gratis?',
      a:
        'Sí. Sin registro, sin tier de pago, sin límite de uso. El sitio se sostiene con publicidad y donaciones opcionales — eso es todo. Toda la funcionalidad está disponible en cada visita.',
    },
    {
      q: '¿A dónde van mis recibos?',
      a:
        'A ningún sitio. La imagen se descodifica, se hace OCR, se analiza y se exporta completamente dentro de tu navegador. No hay paso de subida ni procesamiento del lado del servidor — abre el panel de Red de las DevTools durante un escaneo y verás que ninguna petición transporta los bytes de la imagen.',
    },
    {
      q: '¿Qué idiomas y monedas soporta?',
      a:
        'Receipt Ripper se construye y prueba sobre recibos en inglés, español, francés, alemán, neerlandés, italiano y portugués. La detección de moneda cubre EUR, USD, GBP, CHF, JPY y las formas escritas con palabras (Euro, Dollar, Franc).',
    },
    {
      q: '¿Funciona con fotos del iPhone?',
      a:
        'Sí. Las fotos HEIC / HEIF del iPhone se descodifican en cliente mediante libheif-js y se procesan igual que las JPG. El botón de cámara móvil del dropzone abre directamente la cámara trasera para escanear un recibo y verlo analizado al instante.',
    },
    {
      q: '¿Puedo usarlo sin conexión?',
      a:
        'Tras la primera visita, sí. El motor OCR Tesseract y los paquetes de idioma se cachean en el navegador, así que los escaneos siguientes no necesitan conexión. Sin conexión faltará el banner publicitario y la analítica; el procesado de recibos sigue funcionando.',
    },
    {
      q: '¿Puedo usarlo para impuestos?',
      a:
        'Mucha gente lo hace. La hoja "Resumen" del XLSX agrupa recibos por categoría y moneda con totales acumulados; las hojas de detalle por recibo dan a tu contable las líneas que necesita. El interruptor "Excluir IVA de las exportaciones" oculta las columnas de IVA cuando entregar esos datos a terceros es problemático en tu jurisdicción.',
    },
    {
      q: '¿Cómo gestiona las fotos malas?',
      a:
        'Papel arrugado, marcas de pliegue, ligeras inclinaciones y la mayoría de fotos sobre superficies con textura se gestionan automáticamente — hay un pipeline de detección de esquinas en dos niveles (líneas Hough primero, contorno por inundación de bordes como respaldo). Para recibos que el auto-detect no puede recortar, cada sesión tiene un botón "Recortar manualmente" que abre un selector de esquinas interactivo.',
    },
    {
      q: '¿Y con varios recibos a la vez?',
      a:
        'Suéltalos todos en el dropzone, o suelta un archivo ZIP que los contenga. Se procesan en paralelo mediante un pool de workers dimensionado a tu hardware (2 workers en móvil / RAM baja, hardwareConcurrency − 2 en escritorio). La exportación "Descargar ZIP" agrupa cada imagen procesada junto a un único libro — práctico para archivar un trimestre completo de una vez.',
    },
    {
      q: '¿Por qué la descarga espera 5 segundos?',
      a:
        'Las herramientas gratuitas con publicidad cuestan algo. El aviso previo a la descarga ofrece una donación con un clic mediante Stripe Payment Link antes de que se descargue tu archivo. El botón Saltar está ahí si tienes prisa — la descarga arranca en cuanto termina la cuenta atrás de todos modos.',
    },
    {
      q: '¿El código fuente es abierto?',
      a:
        'El motor OCR (Tesseract.js), el lector PDF (pdf.js de Mozilla), el descodificador HEIC (libheif-js) y el escritor de Excel (SheetJS xlsx) son todos de código abierto — consulta el enlace de licencias del pie de página para la lista de atribuciones completa.',
    },
  ],
  feedback: {
    heading: 'Solicitudes de funciones y recibos difíciles',
    body:
      '¿Tienes una idea para una función, o un recibo que no se escaneó correctamente? Escríbenos a {email} — cada recibo problemático que compartas nos ayuda a mejorar el motor de detección.',
  },
};

const fra: MarketingStrings = {
  hero: {
    title: 'Scanner de reçus privé et gratuit — convertissez photos et PDF en CSV, Excel, ZIP',
    subtitle:
      'Receipt Ripper transforme photos et PDF de reçus en données structurées — commerçant, date, articles, totaux, TVA — entièrement dans votre navigateur. Sans téléchargement, sans inscription, sans compte. Gratuit pour toujours.',
  },
  howItWorksHeading: 'Comment ça marche',
  steps: [
    {
      title: '1. Déposez le reçu',
      body:
        "Glissez une photo (JPG, PNG, HEIC, ou même HEIF d'iPhone), déposez un PDF, ou collez depuis le presse-papiers. Plusieurs reçus à la fois fonctionnent aussi — déposez un lot ou même une archive ZIP des photos du dernier trimestre.",
    },
    {
      title: '2. L’OCR tourne dans votre navigateur',
      body:
        "Tesseract.js traite chaque reçu dans un Web Worker. Les photos sont recadrées automatiquement, leur perspective corrigée, et lues de bout en bout sur votre appareil. Rien n'est téléversé vers un serveur.",
    },
    {
      title: '3. Vérifiez et exportez',
      body:
        "Modifiez tout champ mal lu, taguez les reçus par catégorie, puis téléchargez CSV, Excel (.xlsx) ou un ZIP qui regroupe un classeur et chaque image originale — prêt pour votre comptable.",
    },
  ],
  whyLocalHeading: 'Pourquoi l’OCR local change tout',
  whyLocalParagraphs: [
    "La plupart des scanners de reçus fonctionnent en téléversant votre image vers un pipeline OCR cloud. Leur serveur reçoit la photo, exécute l'OCR à distance et renvoie les données structurées. Ça marche, mais cela veut dire que vos reçus se retrouvent sur le matériel de quelqu'un d'autre : les quatre derniers chiffres de la carte, l'emplacement des magasins, vos habitudes d'achat, les dates et heures où vous étiez quelque part.",
    "Receipt Ripper prend la position inverse. L'OCR tourne sur votre appareil. Les données analysées vivent dans l'onglet de votre navigateur et sont exportées directement vers votre système de fichiers. Aucune requête API ne transporte d'octets de reçus — c'est structurellement impossible parce que la Content Security Policy du site refuse les connexions réseau externes pour tout ce qui pourrait transporter du contenu de reçu.",
    "Cela rend Receipt Ripper adapté à toute personne dont les données de reçus sont sensibles : chefs d'entreprise dont les dépenses ne doivent pas être indexées, indépendants dont la facturation client est privée, particuliers préparant leurs impôts, professionnels en secteur fortement régulé, et toute personne qui préfère simplement que ses affaires restent les siennes.",
  ],
  useCasesHeading: 'Cas d’usage',
  useCases: [
    {
      key: 'tax',
      title: 'Déclaration fiscale',
      body:
        "La déclaration de fin d'année consiste à transformer une boîte à chaussures de reçus en lignes de dépenses déductibles. Receipt Ripper traite l'équivalent d'un trimestre en quelques minutes. Le sous-total, la TVA et le total sont extraits par reçu et additionnés dans l'export Excel — votre comptable obtient un classeur qui se pivote proprement.",
    },
    {
      key: 'expense',
      title: 'Notes de frais',
      body:
        'Suivi des dépenses remboursables après un déplacement client. Tirez les reçus de votre téléphone, corrigez le nom du commerçant si l’OCR l’a malmené, cliquez sur Exporter. Le ZIP donne à votre service financier un classeur avec chaque image originale jointe — sans étape « retrouver la photo correspondante ».',
    },
    {
      key: 'freelance',
      title: 'Comptabilité freelance',
      body:
        "La comptabilité d'un indépendant est l'accumulation lente de petits achats. Déposez chaque reçu au fur et à mesure, taguez par projet ou client, et le lot en cours additionne les totaux pertinents. Activez « Exclure la TVA des exports » si votre juridiction déconseille de remettre des données fiscalement détaillées à des tiers.",
    },
    {
      key: 'split',
      title: 'Partager une note',
      body:
        "Dîner de groupe où chacun veut sa part détaillée. Receipt Ripper extrait chaque ligne avec quantité et prix, ce qui permet de partager selon ce que chacun a réellement commandé plutôt que de diviser le total. Fonctionne sur les reçus en espagnol, français, allemand, italien, portugais et néerlandais aussi bien qu'en anglais.",
    },
  ],
  faqHeading: 'Questions fréquentes',
  faq: [
    {
      q: 'Receipt Ripper est-il vraiment gratuit ?',
      a:
        'Oui. Sans inscription, sans offre payante, sans limite d’usage. Le site vit de la publicité et des dons optionnels — c’est tout. Toutes les fonctionnalités sont disponibles à chaque visite.',
    },
    {
      q: 'Où vont mes reçus ?',
      a:
        "Nulle part. L'image est décodée, OCR'd, analysée et exportée entièrement dans votre navigateur. Pas d'étape de téléversement, pas de traitement côté serveur — ouvrez le panneau Réseau des DevTools pendant un scan et vous verrez qu'aucune requête ne transporte les octets de l'image.",
    },
    {
      q: 'Quelles langues et devises sont prises en charge ?',
      a:
        'Receipt Ripper est construit et testé sur des reçus en anglais, espagnol, français, allemand, néerlandais, italien et portugais. La détection de devise couvre EUR, USD, GBP, CHF, JPY et les formes en toutes lettres (Euro, Dollar, Franc).',
    },
    {
      q: 'Ça marche avec les photos iPhone ?',
      a:
        "Oui. Les photos HEIC / HEIF d'iPhone sont décodées côté client via libheif-js et traitées comme les JPG. Le bouton appareil photo mobile du dropzone ouvre directement la caméra arrière pour scanner un reçu et le voir analysé immédiatement.",
    },
    {
      q: 'Peut-on l’utiliser hors ligne ?',
      a:
        "Après la première visite, oui. Le moteur OCR Tesseract et les paquets de langue sont mis en cache dans le navigateur, les scans suivants n'ont donc pas besoin de connexion. Hors ligne, le bandeau publicitaire et l'analytique sont absents ; le traitement des reçus, lui, reste opérationnel.",
    },
    {
      q: 'Puis-je l’utiliser pour mes impôts ?',
      a:
        'Beaucoup le font. La feuille « Résumé » du XLSX regroupe les reçus par catégorie et devise avec totaux ; les feuilles de détail par reçu donnent à votre comptable la granularité dont il a besoin. Le bouton « Exclure la TVA des exports » masque les colonnes de TVA quand transmettre ces données à des tiers est délicat dans votre juridiction.',
    },
    {
      q: 'Comment gérez-vous les mauvaises photos ?',
      a:
        "Papier froissé, marques de pliure, légères inclinaisons et la plupart des photos prises sur surface texturée sont gérés automatiquement — il y a un pipeline de détection des coins à deux niveaux (lignes de Hough d'abord, contour par remplissage en repli). Pour les reçus que la détection automatique n'arrive pas à recadrer, chaque session a un bouton « Recadrer manuellement » qui ouvre un sélecteur de coins interactif.",
    },
    {
      q: 'Et plusieurs reçus à la fois ?',
      a:
        'Déposez-les tous sur la zone de drop, ou déposez une archive ZIP qui les contient. Ils sont traités en parallèle par un pool de workers dimensionné à votre matériel (2 workers sur mobile / RAM faible, hardwareConcurrency − 2 sur desktop). L\'export « Télécharger ZIP » regroupe chaque image traitée à côté d\'un unique classeur — pratique pour archiver un trimestre d\'un coup.',
    },
    {
      q: 'Pourquoi le téléchargement attend 5 secondes ?',
      a:
        "Les outils gratuits financés par la publicité ont un coût. Le pop-up avant export propose un don en un clic via Stripe Payment Link avant le téléchargement. Le bouton Passer est là si vous êtes pressé — le téléchargement démarre dès que le compte à rebours arrive à zéro de toute façon.",
    },
    {
      q: 'Le code source est-il ouvert ?',
      a:
        "Le moteur OCR (Tesseract.js), le lecteur PDF (pdf.js de Mozilla), le décodeur HEIC (libheif-js) et l'auteur Excel (SheetJS xlsx) sont tous open source — consultez le lien des licences en pied de page pour la liste complète des attributions.",
    },
  ],
  feedback: {
    heading: 'Demandes de fonctionnalités et reçus difficiles',
    body:
      "Une idée de fonctionnalité, ou un reçu qui n'a pas été correctement scanné ? Écrivez-nous à {email} — chaque reçu problématique que vous partagez nous aide à améliorer le moteur de détection.",
  },
};

const deu: MarketingStrings = {
  hero: {
    title: 'Kostenloser privater Beleg-Scanner — Fotos und PDFs in CSV, Excel oder ZIP umwandeln',
    subtitle:
      'Receipt Ripper macht aus Beleg-Fotos und PDFs strukturierte Daten — Händler, Datum, Posten, Beträge, MwSt — komplett in Ihrem Browser. Kein Upload, keine Anmeldung, kein Konto. Dauerhaft kostenlos.',
  },
  howItWorksHeading: 'So funktioniert’s',
  steps: [
    {
      title: '1. Beleg ablegen',
      body:
        'Ziehen Sie ein Foto (JPG, PNG, HEIC, sogar iPhone HEIF), legen Sie ein PDF ab oder fügen Sie aus der Zwischenablage ein. Mehrere Belege auf einmal funktionieren auch — ein Stapel oder ein ZIP-Archiv mit den Fotos des letzten Quartals.',
    },
    {
      title: '2. OCR läuft im Browser',
      body:
        'Tesseract.js verarbeitet jeden Beleg in einem Web Worker. Fotos werden automatisch zugeschnitten, perspektivisch korrigiert und Ende-zu-Ende auf Ihrem Gerät gelesen. Nichts wird auf einen Server hochgeladen.',
    },
    {
      title: '3. Prüfen und exportieren',
      body:
        'Korrigieren Sie beliebige Felder, taggen Sie Belege mit Kategorien und laden Sie CSV, Excel (.xlsx) oder ein ZIP herunter, das Arbeitsmappe und Originalbild bündelt — fertig für Ihre Buchhaltung.',
    },
  ],
  whyLocalHeading: 'Warum lokale OCR den Unterschied macht',
  whyLocalParagraphs: [
    'Die meisten Beleg-Scanner laden Ihr Bild in eine Cloud-OCR-Pipeline. Deren Server bekommt das Foto, führt OCR remote aus und liefert strukturierte Daten zurück. Funktioniert, bedeutet aber, dass Ihre Belege auf fremder Hardware liegen: die letzten vier Stellen Ihrer Karte, Ladenstandorte, Kaufmuster, Daten und Uhrzeiten an denen Sie irgendwo waren.',
    'Receipt Ripper geht den umgekehrten Weg. OCR läuft auf Ihrem Gerät. Die geparsten Daten leben im Browser-Tab und werden direkt auf Ihre Festplatte exportiert. Es gibt keinen API-Aufruf, der Beleg-Bytes transportiert — strukturell unmöglich, weil die Content Security Policy externe Netzwerkverbindungen für alles ablehnt, was Beleg-Inhalt tragen könnte.',
    'Damit ist Receipt Ripper passend für alle, deren Beleg-Daten sensibel sind: Unternehmer mit nicht-indexierbaren Ausgabenmustern, Selbstständige mit privater Kundenabrechnung, Privatpersonen bei der Steuererklärung, regulierte Branchen, und alle, die einfach möchten, dass ihre Sachen ihre Sachen bleiben.',
  ],
  useCasesHeading: 'Anwendungsfälle',
  useCases: [
    {
      key: 'tax',
      title: 'Steuererklärung',
      body:
        'Jahresabschluss heißt, einen Schuhkarton voll Belege in Zeilen abzugsfähiger Ausgaben zu verwandeln. Receipt Ripper verarbeitet ein Quartal in Minuten. Zwischensumme, MwSt und Summe werden pro Beleg extrahiert und im Excel-Export aufaddiert — Ihr Steuerberater bekommt eine Arbeitsmappe, die sich sauber pivotieren lässt.',
    },
    {
      key: 'expense',
      title: 'Spesenabrechnung',
      body:
        'Erstattungsfähige Ausgaben unterwegs oder nach einem Kundenbesuch. Belege vom Handy ziehen, Händlernamen korrigieren wenn OCR sie verstümmelt hat, Export drücken. Das ZIP gibt Ihrem Finance-Team eine Arbeitsmappe samt aller Originalbilder — kein zusätzliches "passendes Foto suchen".',
    },
    {
      key: 'freelance',
      title: 'Freelance-Buchhaltung',
      body:
        'Selbstständigen-Buchhaltung ist die langsame Anhäufung kleiner Käufe. Belege ablegen sobald sie kommen, mit Projekt oder Kunden taggen, und der laufende Stapel summiert die relevanten Beträge. „MwSt aus Exporten ausschließen" aktivieren, wenn Ihre Rechtslage Steuer-itemisierte Daten an Dritte ungern sieht.',
    },
    {
      key: 'split',
      title: 'Rechnungen aufteilen',
      body:
        'Gruppen-Essen, bei dem jeder seinen Anteil itemisiert haben will. Receipt Ripper extrahiert jede Zeile mit Menge und Preis, sodass Sie nach dem aufteilen können, was jeder bestellt hat — statt die Summe zu teilen. Funktioniert mit Belegen auf Spanisch, Französisch, Deutsch, Italienisch, Portugiesisch und Niederländisch ebenso wie Englisch.',
    },
  ],
  faqHeading: 'Häufig gestellte Fragen',
  faq: [
    {
      q: 'Ist Receipt Ripper wirklich kostenlos?',
      a:
        'Ja. Keine Anmeldung, keine Bezahlstufe, kein Nutzungslimit. Die Seite finanziert sich über Werbung plus optionale Spenden — das ist alles. Bei jedem Besuch steht der volle Funktionsumfang zur Verfügung.',
    },
    {
      q: 'Wo gehen meine Belege hin?',
      a:
        'Nirgendwohin. Das Bild wird komplett im Browser dekodiert, OCR-verarbeitet, geparst und exportiert. Es gibt keinen Upload und keine serverseitige Verarbeitung — öffnen Sie das DevTools-Netzwerk-Panel während eines Scans und Sie sehen, dass keine Anfrage die Bild-Bytes transportiert.',
    },
    {
      q: 'Welche Sprachen und Währungen werden unterstützt?',
      a:
        'Receipt Ripper wird mit Belegen auf Englisch, Spanisch, Französisch, Deutsch, Niederländisch, Italienisch und Portugiesisch entwickelt und getestet. Die Währungserkennung deckt EUR, USD, GBP, CHF, JPY und die ausgeschriebenen Formen ab (Euro, Dollar, Franc).',
    },
    {
      q: 'Funktioniert es mit iPhone-Fotos?',
      a:
        'Ja. iPhone HEIC / HEIF-Fotos werden clientseitig über libheif-js dekodiert und genauso verarbeitet wie JPGs. Der mobile Kamera-Button im Dropzone öffnet direkt die Rückkamera, damit Sie einen Beleg scannen und sofort die Auswertung sehen.',
    },
    {
      q: 'Funktioniert es offline?',
      a:
        'Nach dem ersten Besuch ja. Die Tesseract-OCR-Engine und die Sprachpakete cachen im Browser, weitere Scans benötigen keine Verbindung. Offline fehlen Werbebanner und Analytics; die Beleg-Verarbeitung selbst ist davon nicht betroffen.',
    },
    {
      q: 'Kann ich es für die Steuer nutzen?',
      a:
        'Viele tun es. Das „Summary"-Blatt der XLSX gruppiert Belege nach Kategorie und Währung mit Gesamtsummen; die Detailblätter pro Beleg liefern Ihrem Steuerberater die Posten-Granularität. Der Schalter „MwSt aus Exporten ausschließen" blendet die MwSt-Spalten aus, wenn das Weitergeben dieser Daten an Dritte in Ihrer Rechtslage problematisch ist.',
    },
    {
      q: 'Wie geht es mit schlechten Fotos um?',
      a:
        'Zerknitterte Belege, Knickspuren, leichte Verkippungen und die meisten Fotos auf strukturierten Oberflächen werden automatisch behandelt — es gibt eine zweistufige Eckenerkennung (zuerst Hough-Linien, dann Edge-Contour-Flutfüllung als Rückfalloption). Für Belege, die die Auto-Erkennung nicht zuschneiden kann, hat jede Session einen „Manuell zuschneiden"-Button, der einen interaktiven Eckenwähler öffnet.',
    },
    {
      q: 'Wie sieht es mit mehreren Belegen auf einmal aus?',
      a:
        'Alle in den Dropzone werfen, oder ein ZIP-Archiv mit den Bildern / PDFs ablegen. Sie werden parallel von einem Worker-Pool verarbeitet, der zur Hardware passt (2 Worker mobil / wenig RAM, hardwareConcurrency − 2 auf Desktop). Der „Download ZIP"-Export bündelt jedes verarbeitete Bild zusammen mit einer einzigen Arbeitsmappe — praktisch, um ein Quartal auf einmal zu archivieren.',
    },
    {
      q: 'Warum wartet der Download 5 Sekunden?',
      a:
        'Kostenlose werbefinanzierte Tools verursachen Kosten. Das Pre-Export-Popup bietet eine Ein-Klick-Spende über Stripe Payment Link an, bevor die Datei herunterlädt. Der „Überspringen"-Button ist da, wenn Sie es eilig haben — der Download startet ohnehin sobald der Countdown abläuft.',
    },
    {
      q: 'Ist der Quellcode offen?',
      a:
        'Die OCR-Engine (Tesseract.js), der PDF-Reader (Mozillas pdf.js), der HEIC-Decoder (libheif-js) und der Excel-Writer (SheetJS xlsx) sind alle Open Source — siehe den Lizenz-Link im Footer für die vollständige Attributionsliste.',
    },
  ],
  feedback: {
    heading: 'Feature-Wünsche & schwierige Belege',
    body:
      'Haben Sie eine Idee für eine Funktion oder einen Beleg, der nicht korrekt gescannt wurde? Schreiben Sie uns an {email} — jeder problematische Beleg, den Sie teilen, hilft uns, die Erkennungs-Engine zu verbessern.',
  },
};

const nld: MarketingStrings = {
  hero: {
    title: 'Gratis privé bonscanner — converteer foto’s en PDF’s naar CSV, Excel of ZIP',
    subtitle:
      'Receipt Ripper zet bonfoto’s en PDF’s om naar gestructureerde data — leverancier, datum, regels, totalen, BTW — volledig in je browser. Geen upload, geen registratie, geen account. Voor altijd gratis.',
  },
  howItWorksHeading: 'Hoe het werkt',
  steps: [
    {
      title: '1. Sleep de bon erin',
      body:
        'Sleep een foto (JPG, PNG, HEIC, ook iPhone HEIF), zet een PDF neer of plak vanaf het klembord. Meerdere bonnen tegelijk kan ook — een batch of zelfs een ZIP-archief met de foto’s van het vorige kwartaal.',
    },
    {
      title: '2. OCR draait in je browser',
      body:
        'Tesseract.js verwerkt elke bon in een Web Worker. Foto’s worden automatisch bijgesneden, het perspectief gecorrigeerd en end-to-end op je apparaat gelezen. Niets wordt naar een server geüpload.',
    },
    {
      title: '3. Controleer en exporteer',
      body:
        'Pas verkeerd gelezen velden aan, label bonnen met categorieën en download CSV, Excel (.xlsx) of een ZIP met werkmap plus elk origineel beeld — klaar voor je boekhouder.',
    },
  ],
  whyLocalHeading: 'Waarom lokale OCR ertoe doet',
  whyLocalParagraphs: [
    'De meeste bonscanners werken door je beeld naar een cloud-OCR-pipeline te uploaden. Hun server ontvangt de foto, draait OCR op afstand en geeft gestructureerde data terug. Werkt, maar betekent dat je bonnen op andermans hardware staan: laatste vier cijfers van kaarten, winkellocaties, koopgewoontes, data en tijden waarop je ergens was.',
    'Receipt Ripper kiest het tegenovergestelde. OCR draait op je apparaat. De geparseerde data leeft in je browsertabblad en wordt direct naar je bestandssysteem geëxporteerd. Er is geen API-call die bon-bytes vervoert — structureel onmogelijk, omdat het Content Security Policy van de site externe netwerkverbindingen afwijst voor alles wat boninhoud zou kunnen vervoeren.',
    'Daarmee past Receipt Ripper bij iedereen wiens bondata gevoelig is: ondernemers met uitgavenpatronen die niet geïndexeerd mogen worden, zelfstandigen met privé klant-facturatie, particulieren met belastingaangifte, mensen in sterk gereguleerde sectoren, en iedereen die simpelweg liever wil dat zijn spullen van hem blijven.',
  ],
  useCasesHeading: 'Toepassingen',
  useCases: [
    {
      key: 'tax',
      title: 'Belastingaangifte',
      body:
        'Jaarafsluiting betekent een schoenendoos vol bonnen omzetten naar aftrekposten. Receipt Ripper verwerkt een kwartaal in minuten. Subtotaal, BTW en totaal worden per bon geëxtraheerd en in de Excel-export gesommeerd — je boekhouder krijgt een werkmap die schoon pivoteert.',
    },
    {
      key: 'expense',
      title: 'Declaraties',
      body:
        'Onkostendeclaratie onderweg of na een klantbezoek. Sleep de bonnen van je telefoon, corrigeer de leveranciersnaam als OCR hem verminkt heeft, druk op Exporteren. Het ZIP geeft je financeteam een werkmap plus elk origineel beeld erbij — geen aparte "bijbehorende foto zoeken"-stap.',
    },
    {
      key: 'freelance',
      title: 'ZZP-boekhouding',
      body:
        'Boekhouding voor zelfstandigen is een trage stapel kleine aankopen. Zet elke bon erin zodra je hem krijgt, label per project of klant, en de lopende batch sommeert de relevante totalen. Zet "BTW uitsluiten van exports" aan als jouw jurisdictie het delen van BTW-uitgesplitste data met derden afraadt.',
    },
    {
      key: 'split',
      title: 'Rekening delen',
      body:
        'Groepsdiner waar iedereen zijn deel uitgesplitst wil. Receipt Ripper haalt elke regel met aantal en prijs eruit, zodat je kunt verdelen op wat ieder daadwerkelijk besteld heeft in plaats van het totaal te delen. Werkt op bonnen in het Spaans, Frans, Duits, Italiaans, Portugees en Nederlands net zo goed als Engels.',
    },
  ],
  faqHeading: 'Veelgestelde vragen',
  faq: [
    {
      q: 'Is Receipt Ripper echt gratis?',
      a:
        'Ja. Geen registratie, geen betaalde tier, geen gebruikslimiet. De site draait op advertenties plus optionele donaties — that\'s it. De volledige feature-set is bij elk bezoek beschikbaar.',
    },
    {
      q: 'Waar gaan mijn bonnen heen?',
      a:
        'Nergens. Het beeld wordt gedecodeerd, OCR\'d, geparseerd en geëxporteerd, allemaal binnen je browser. Geen uploadstap en geen serververwerking — open het Network-paneel in je DevTools tijdens een scan en je ziet dat geen enkele request de beeld-bytes ergens heen brengt.',
    },
    {
      q: 'Welke talen en valuta worden ondersteund?',
      a:
        'Receipt Ripper wordt gebouwd en getest op bonnen in het Engels, Spaans, Frans, Duits, Nederlands, Italiaans en Portugees. Valuta-detectie dekt EUR, USD, GBP, CHF, JPY en de uitgeschreven vormen (Euro, Dollar, Franc).',
    },
    {
      q: 'Werkt het op iPhone-foto\'s?',
      a:
        'Ja. iPhone HEIC / HEIF-foto\'s worden client-side gedecodeerd via libheif-js en identiek aan JPG\'s verwerkt. De mobiele cameraknop in de dropzone opent direct de achtercamera, zodat je een bon kunt scannen en hem meteen geparseerd ziet.',
    },
    {
      q: 'Kan ik het offline gebruiken?',
      a:
        'Na het eerste bezoek wel. De Tesseract-OCR-engine en taalpakketten cachen in de browser, dus latere scans hebben geen verbinding nodig. Offline ontbreken de advertentiebanner en analytics; de bonverwerking blijft werken.',
    },
    {
      q: 'Kan ik het voor belastingen gebruiken?',
      a:
        'Veel mensen doen dat. Het "Summary"-blad van het XLSX groepeert bonnen per categorie en valuta met grand totals; de per-bon-detailbladen geven je boekhouder de regelniveaus die hij nodig heeft. De "BTW uitsluiten van exports"-schakelaar verbergt BTW-kolommen wanneer het delen van die data met derden in jouw jurisdictie problematisch is.',
    },
    {
      q: 'Hoe gaan jullie om met slechte foto\'s?',
      a:
        'Verkreukeld papier, vouwen, lichte scheefstand en de meeste foto\'s op getextureerde oppervlakken worden automatisch afgehandeld — er is een tweetraps hoekdetectie-pipeline (eerst Hough-lijnen, dan edge-contour flood-fill als terugval). Voor bonnen die de auto-detectie niet kan bijsnijden, heeft elke sessie een "Handmatig bijsnijden"-knop die een interactieve hoekenkiezer opent.',
    },
    {
      q: 'En meerdere bonnen tegelijk?',
      a:
        'Sleep ze allemaal naar de dropzone, of laat een ZIP-archief met de afbeeldingen / PDF\'s vallen. Ze worden parallel verwerkt door een worker pool op maat van je hardware (2 workers mobiel / weinig RAM, hardwareConcurrency − 2 op desktop). De "Download ZIP"-export bundelt elk verwerkt beeld naast één werkmap — handig om een heel kwartaal in één keer te archiveren.',
    },
    {
      q: 'Waarom wacht de download 5 seconden?',
      a:
        'Gratis door advertenties gefinancierde tools kosten iets. De pre-export-prompt biedt vóór je download een eenklik-donatie via Stripe Payment Link aan. Skip staat er als je haast hebt — de download start zodra de countdown afloopt sowieso.',
    },
    {
      q: 'Is de broncode open?',
      a:
        'De OCR-engine (Tesseract.js), de PDF-reader (Mozilla\'s pdf.js), de HEIC-decoder (libheif-js) en de Excel-writer (SheetJS xlsx) zijn allemaal open source — zie de licentielink in de footer voor de volledige attributielijst.',
    },
  ],
  feedback: {
    heading: 'Functiewensen & lastige bonnetjes',
    body:
      'Heb je een idee voor een functie of een bonnetje dat niet goed werd gescand? Mail ons op {email} — elk probleembonnetje dat je deelt helpt ons de detectie te verbeteren.',
  },
};

const ita: MarketingStrings = {
  hero: {
    title: 'Scanner di scontrini privato e gratuito — converti foto e PDF in CSV, Excel, ZIP',
    subtitle:
      'Receipt Ripper trasforma foto e PDF di scontrini in dati strutturati — esercente, data, voci, totali, IVA — interamente nel tuo browser. Nessun upload, nessuna registrazione, nessun account. Gratis per sempre.',
  },
  howItWorksHeading: 'Come funziona',
  steps: [
    {
      title: '1. Trascina lo scontrino',
      body:
        'Trascina una foto (JPG, PNG, HEIC, anche HEIF di iPhone), rilascia un PDF o incolla dagli appunti. Anche più scontrini insieme — un batch o un archivio ZIP con le foto del trimestre scorso.',
    },
    {
      title: '2. L’OCR gira nel tuo browser',
      body:
        'Tesseract.js elabora ogni scontrino in un Web Worker. Le foto vengono ritagliate automaticamente, la prospettiva corretta e lette end-to-end sul tuo dispositivo. Niente viene caricato su alcun server.',
    },
    {
      title: '3. Controlla ed esporta',
      body:
        'Modifica eventuali campi sbagliati, etichetta gli scontrini per categoria e scarica CSV, Excel (.xlsx) o un ZIP che impacchetta una cartella di lavoro più ogni immagine originale — pronto per il tuo commercialista.',
    },
  ],
  whyLocalHeading: 'Perché l’OCR locale fa la differenza',
  whyLocalParagraphs: [
    'La maggior parte degli scanner di scontrini funziona caricando l\'immagine su una pipeline OCR cloud. Il loro server riceve la foto, esegue OCR remoto e restituisce dati strutturati. Funziona, ma vuol dire che gli scontrini stanno sull\'hardware di qualcun altro: ultime quattro cifre delle carte, posizioni dei negozi, abitudini di spesa, date e orari in cui eri da qualche parte.',
    'Receipt Ripper prende la posizione opposta. L\'OCR gira sul tuo dispositivo. I dati estratti vivono nella scheda del browser e vengono esportati direttamente sul tuo filesystem. Non c\'è alcuna chiamata API che porti byte di scontrini — è strutturalmente impossibile perché la Content Security Policy del sito rifiuta connessioni di rete esterne per qualsiasi cosa che possa trasportare contenuto di scontrini.',
    'Questo rende Receipt Ripper adatto a chiunque abbia dati di scontrini sensibili: imprenditori con pattern di spesa che non vogliono indicizzati, professionisti la cui fatturazione cliente è privata, privati che fanno la dichiarazione, lavoratori in settori altamente regolati, e chiunque preferisca semplicemente che le sue cose restino sue.',
  ],
  useCasesHeading: 'Casi d’uso',
  useCases: [
    {
      key: 'tax',
      title: 'Dichiarazione dei redditi',
      body:
        'La dichiarazione di fine anno significa trasformare una scatola di scontrini in righe di spese deducibili. Receipt Ripper processa l\'equivalente di un trimestre in minuti. Imponibile, IVA e totale vengono estratti per scontrino e sommati nell\'export Excel — il tuo commercialista riceve una cartella di lavoro che pivota in modo pulito.',
    },
    {
      key: 'expense',
      title: 'Note spese',
      body:
        'Tracciamento di spese rimborsabili in trasferta o dopo una visita cliente. Trascina gli scontrini dal telefono, correggi i nomi degli esercenti se l\'OCR li ha massacrati, premi Esporta. Il ZIP dà al tuo team finance una cartella di lavoro più ogni immagine originale allegata — senza il passaggio "trova la foto corrispondente".',
    },
    {
      key: 'freelance',
      title: 'Contabilità freelance',
      body:
        'La contabilità di un libero professionista è una lenta accumulazione di piccoli acquisti. Inserisci ogni scontrino appena lo ricevi, etichetta per progetto o cliente, e il batch in corso somma i totali rilevanti. Attiva "Escludi IVA dalle esportazioni" se la tua giurisdizione vede male la consegna a terzi di dati IVA dettagliati.',
    },
    {
      key: 'split',
      title: 'Dividere i conti',
      body:
        'Cena di gruppo dove ognuno vuole la sua parte dettagliata. Receipt Ripper estrae ogni voce con quantità e prezzo, così puoi dividere per ciò che ognuno ha effettivamente ordinato invece di spezzare il totale. Funziona con scontrini in spagnolo, francese, tedesco, italiano, portoghese e olandese oltre all\'inglese.',
    },
  ],
  faqHeading: 'Domande frequenti',
  faq: [
    {
      q: 'Receipt Ripper è davvero gratis?',
      a:
        'Sì. Nessuna registrazione, nessun piano a pagamento, nessun limite di utilizzo. Il sito vive di pubblicità più donazioni opzionali — tutto qui. L\'intera funzionalità è disponibile a ogni visita.',
    },
    {
      q: 'Dove vanno i miei scontrini?',
      a:
        'Da nessuna parte. L\'immagine viene decodificata, processata via OCR, parserizzata ed esportata interamente dentro il tuo browser. Nessun upload, nessun processing server-side — apri il pannello Rete delle DevTools durante una scansione e vedrai che nessuna richiesta porta i byte dell\'immagine altrove.',
    },
    {
      q: 'Quali lingue e valute supporta?',
      a:
        'Receipt Ripper è costruito e testato su scontrini in inglese, spagnolo, francese, tedesco, olandese, italiano e portoghese. La rilevazione di valuta copre EUR, USD, GBP, CHF, JPY e le forme scritte (Euro, Dollar, Franc).',
    },
    {
      q: 'Funziona con foto da iPhone?',
      a:
        'Sì. Le foto HEIC / HEIF di iPhone vengono decodificate client-side tramite libheif-js e processate identicamente alle JPG. Il pulsante fotocamera mobile nella dropzone apre direttamente la fotocamera posteriore, così scansioni uno scontrino e lo vedi parserizzato all\'istante.',
    },
    {
      q: 'Posso usarlo offline?',
      a:
        'Dopo la prima visita, sì. Il motore OCR Tesseract e i pacchetti lingua vengono cachati nel browser, quindi le scansioni successive non richiedono connessione. Offline mancano il banner pubblicitario e le analytics; il processing degli scontrini funziona lo stesso.',
    },
    {
      q: 'Posso usarlo per le tasse?',
      a:
        'Molti lo fanno. Il foglio "Summary" del XLSX raggruppa gli scontrini per categoria e valuta con totali generali; i fogli di dettaglio per scontrino danno al tuo commercialista la granularità delle voci che gli serve. L\'interruttore "Escludi IVA dalle esportazioni" nasconde le colonne IVA quando consegnare quei dati a terzi è problematico nella tua giurisdizione.',
    },
    {
      q: 'Come gestite le foto cattive?',
      a:
        'Carta sgualcita, segni di piega, leggere inclinazioni e la maggior parte delle foto su superfici testurizzate vengono gestite automaticamente — c\'è una pipeline di rilevamento angoli a due livelli (linee di Hough prima, contorno per riempimento di bordi come fallback). Per scontrini che l\'auto-detect non riesce a ritagliare, ogni sessione ha un pulsante "Ritaglia manualmente" che apre un selettore di angoli interattivo.',
    },
    {
      q: 'E con più scontrini insieme?',
      a:
        'Trascinali tutti nella dropzone, o lascia cadere un archivio ZIP che li contiene. Vengono processati in parallelo tramite un pool di worker dimensionato al tuo hardware (2 worker su mobile / poca RAM, hardwareConcurrency − 2 su desktop). L\'export "Download ZIP" impacchetta ogni immagine processata accanto a un\'unica cartella di lavoro — comodo per archiviare un trimestre alla volta.',
    },
    {
      q: 'Perché il download aspetta 5 secondi?',
      a:
        'Gli strumenti gratuiti supportati da pubblicità hanno un costo. Il prompt pre-export propone una donazione one-click via Stripe Payment Link prima del download. Salta è lì se hai fretta — il download parte non appena finisce il countdown comunque.',
    },
    {
      q: 'Il codice sorgente è aperto?',
      a:
        'Il motore OCR (Tesseract.js), il lettore PDF (pdf.js di Mozilla), il decoder HEIC (libheif-js) e lo writer Excel (SheetJS xlsx) sono tutti open source — vedi il link delle licenze nel footer per l\'elenco completo delle attribuzioni.',
    },
  ],
  feedback: {
    heading: 'Richieste di funzionalità e ricevute difficili',
    body:
      "Hai un'idea per una funzionalità o una ricevuta che non è stata scansionata correttamente? Scrivici a {email} — ogni ricevuta problematica che condividi ci aiuta a migliorare il motore di rilevamento.",
  },
};

const por: MarketingStrings = {
  hero: {
    title: 'Scanner de recibos privado e gratuito — converta fotos e PDFs em CSV, Excel ou ZIP',
    subtitle:
      'Receipt Ripper transforma fotos e PDFs de recibos em dados estruturados — comerciante, data, linhas, totais, IVA — inteiramente no seu navegador. Sem upload, sem registo, sem conta. Sempre grátis.',
  },
  howItWorksHeading: 'Como funciona',
  steps: [
    {
      title: '1. Largue o recibo',
      body:
        'Arraste uma foto (JPG, PNG, HEIC, mesmo HEIF do iPhone), largue um PDF ou cole da área de transferência. Vários recibos de uma vez também — um lote ou mesmo um arquivo ZIP com as fotos do último trimestre.',
    },
    {
      title: '2. O OCR corre no seu navegador',
      body:
        'O Tesseract.js processa cada recibo dentro de um Web Worker. As fotos são recortadas automaticamente, a perspetiva corrigida e lidas de ponta a ponta no seu dispositivo. Nada é enviado para qualquer servidor.',
    },
    {
      title: '3. Reveja e exporte',
      body:
        'Edite qualquer campo que tenha vindo errado, etiquete recibos por categoria e descarregue CSV, Excel (.xlsx) ou um ZIP que junta uma pasta de trabalho mais cada imagem original — pronto para o seu contabilista.',
    },
  ],
  whyLocalHeading: 'Porque é que o OCR local importa',
  whyLocalParagraphs: [
    'A maioria dos scanners de recibos funcionam enviando a sua imagem para uma pipeline de OCR na nuvem. O servidor deles recebe a foto, executa OCR remotamente e devolve os dados estruturados. Funciona, mas significa que os recibos ficam no hardware de outra pessoa: últimos quatro dígitos do cartão, localizações de lojas, padrões de compra, datas e horas em que esteve em algum sítio.',
    'O Receipt Ripper toma a posição oposta. O OCR corre no seu dispositivo. Os dados analisados vivem no separador do navegador e são exportados diretamente para o seu sistema de ficheiros. Não há chamada de API a transportar bytes de recibos — é estruturalmente impossível porque a Content Security Policy do site recusa ligações de rede externas para tudo o que possa transportar conteúdo de recibo.',
    'Isso torna o Receipt Ripper adequado para quem tenha dados de recibos sensíveis: empresários com padrões de despesa que não querem indexados, freelancers cuja faturação a clientes é privada, particulares a tratar de impostos, profissionais em setores muito regulados, e qualquer pessoa que simplesmente prefira que as suas coisas continuem a ser suas.',
  ],
  useCasesHeading: 'Casos de uso',
  useCases: [
    {
      key: 'tax',
      title: 'Declaração de impostos',
      body:
        'A declaração de fim de ano significa transformar uma caixa de sapatos cheia de recibos em linhas de despesas dedutíveis. O Receipt Ripper processa o equivalente a um trimestre em minutos. Subtotal, IVA e total são extraídos por recibo e somados na exportação Excel — o seu contabilista recebe uma pasta de trabalho que pivota de forma limpa.',
    },
    {
      key: 'expense',
      title: 'Notas de despesa',
      body:
        'Acompanhamento de despesas reembolsáveis em viagem ou após uma visita a cliente. Arraste os recibos do telemóvel, corrija o nome do comerciante se o OCR o estragou, prima Exportar. O ZIP dá à sua equipa financeira uma pasta de trabalho mais cada imagem original anexada — sem o passo separado de "encontrar a foto correspondente".',
    },
    {
      key: 'freelance',
      title: 'Contabilidade freelancer',
      body:
        'A contabilidade de um freelancer é a acumulação lenta de pequenas compras. Largue cada recibo à medida que o recebe, etiquete por projeto ou cliente, e o lote em curso soma os totais relevantes. Ative "Excluir IVA das exportações" se a sua jurisdição não vê com bons olhos a entrega a terceiros de dados detalhados de IVA.',
    },
    {
      key: 'split',
      title: 'Dividir contas',
      body:
        'Jantar de grupo onde toda a gente quer a sua parte detalhada. O Receipt Ripper extrai cada linha com quantidade e preço, para poder dividir por aquilo que cada pessoa realmente pediu em vez de partir o total. Funciona em recibos em espanhol, francês, alemão, italiano, português e neerlandês para além do inglês.',
    },
  ],
  faqHeading: 'Perguntas frequentes',
  faq: [
    {
      q: 'O Receipt Ripper é mesmo grátis?',
      a:
        'Sim. Sem registo, sem nível pago, sem limite de utilização. O site sustenta-se com publicidade mais donativos opcionais — é tudo. Toda a funcionalidade está disponível em cada visita.',
    },
    {
      q: 'Para onde vão os meus recibos?',
      a:
        'Para lado nenhum. A imagem é descodificada, processada por OCR, analisada e exportada inteiramente dentro do seu navegador. Não há passo de upload nem processamento do lado do servidor — abra o painel Rede das DevTools durante uma digitalização e verá que nenhum pedido transporta os bytes da imagem para qualquer sítio.',
    },
    {
      q: 'Que línguas e moedas suporta?',
      a:
        'O Receipt Ripper é construído e testado em recibos em inglês, espanhol, francês, alemão, neerlandês, italiano e português. A deteção de moeda cobre EUR, USD, GBP, CHF, JPY e as formas escritas por extenso (Euro, Dollar, Franc).',
    },
    {
      q: 'Funciona com fotos do iPhone?',
      a:
        'Sim. Fotos HEIC / HEIF de iPhone são descodificadas no cliente via libheif-js e processadas tal como JPGs. O botão de câmara móvel na dropzone abre diretamente a câmara traseira, para que possa digitalizar um recibo e vê-lo analisado de imediato.',
    },
    {
      q: 'Posso usá-lo offline?',
      a:
        'Após a primeira visita, sim. O motor OCR Tesseract e os pacotes de língua ficam em cache no navegador, por isso as digitalizações seguintes não precisam de ligação. Offline faltam o banner publicitário e as analytics; o processamento dos recibos continua a funcionar.',
    },
    {
      q: 'Posso usá-lo para impostos?',
      a:
        'Muitas pessoas usam. A folha "Summary" do XLSX agrupa recibos por categoria e moeda com totais gerais; as folhas de detalhe por recibo dão ao seu contabilista a granularidade de linha que precisa. O interruptor "Excluir IVA das exportações" oculta as colunas de IVA quando a entrega desses dados a terceiros é problemática na sua jurisdição.',
    },
    {
      q: 'Como lidam com fotos más?',
      a:
        'Papel amarrotado, marcas de dobra, ligeiras inclinações e a maioria das fotos sobre superfícies texturadas são tratadas automaticamente — há uma pipeline de deteção de cantos em dois níveis (linhas de Hough primeiro, contorno por preenchimento de bordas como recurso). Para recibos que o auto-detect não consegue recortar, cada sessão tem um botão "Recortar manualmente" que abre um seletor de cantos interativo.',
    },
    {
      q: 'E com vários recibos ao mesmo tempo?',
      a:
        'Largue-os todos na dropzone, ou largue um arquivo ZIP que os contenha. São processados em paralelo por um pool de workers dimensionado ao seu hardware (2 workers em mobile / pouca RAM, hardwareConcurrency − 2 em desktop). A exportação "Download ZIP" junta cada imagem processada juntamente com uma única pasta de trabalho — útil para arquivar um trimestre completo de uma vez.',
    },
    {
      q: 'Porque é que o download espera 5 segundos?',
      a:
        'Ferramentas gratuitas suportadas por publicidade têm um custo. O aviso pré-exportação oferece um donativo num clique via Stripe Payment Link antes do ficheiro descarregar. O botão Saltar está lá se tiver pressa — o download arranca no momento em que a contagem decrescente termina, de qualquer forma.',
    },
    {
      q: 'O código-fonte é aberto?',
      a:
        'O motor OCR (Tesseract.js), o leitor PDF (pdf.js da Mozilla), o descodificador HEIC (libheif-js) e o escritor Excel (SheetJS xlsx) são todos open source — veja o link de licenças no rodapé para a lista completa de atribuições.',
    },
  ],
  feedback: {
    heading: 'Pedidos de funcionalidades e recibos difíceis',
    body:
      'Tem uma ideia para uma funcionalidade ou um recibo que não foi digitalizado corretamente? Envie-nos um email para {email} — cada recibo problemático que partilha ajuda-nos a melhorar o motor de deteção.',
  },
};

export const MARKETING_BY_LANG: Record<UiLangCode, MarketingStrings> = {
  eng,
  spa,
  fra,
  deu,
  nld,
  ita,
  por,
};
